import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
  WrxlRulesService,
  WxrlArea,
  WxrlLaboratory,
  WxrlMode,
  WxrlRuleState,
  WxrlSubject,
  WxrlSubjectType,
} from '../../services/wxrl-rules.service';
import { DropdownChangeEvent } from 'primeng/dropdown';

export type WxrlFilter = {
  labNr: number;
  ruleState?: string;
  subjectType: string;
  subjectID?: string | number;
  mode?: string;
  labBerNr: number;
};

// Tipo para los filtros deshidratados (solo IDs y valores primitivos)
export type DehydratedFilters = {
  selectedLaboratoryId?: number;
  selectedAreaId?: number;
  selectedStatusCode?: string;
  selectedSearchForCode?: string; // Ahora guardamos el código
  selectedTrigger?: WxrlMode;
  selectedSubjectValue?: string | number;
  ruleText?: string;
};

@Component({
  selector: 'afoone-wxrl-rule-list-filters',
  templateUrl: './wxrl-rule-list-filters.component.html',
  styleUrls: ['./wxrl-rule-list-filters.component.css'],
})
export class WxrlRuleListFiltersComponent implements OnInit {
  @Output() public search: EventEmitter<WxrlFilter> = new EventEmitter();
  @Output() public laboratorySelected: EventEmitter<number> = new EventEmitter<number>();
  @Output() public accessDenied: EventEmitter<void> = new EventEmitter<void>();

  private readonly STORAGE_KEY = 'wxrl-filters';

  public laboratories: WxrlLaboratory[] = [];
  public areas: WxrlArea[] = [];
  public statuses: WxrlRuleState[] = [];
  public subjectTypes: WxrlSubjectType[] = [];
  public searchText: string = '';
  public subjects: WxrlSubject[] = [];

  public selectedLaboratory: WxrlLaboratory = null;
  public selectedArea: WxrlArea = null;
  public selectedStatus: WxrlRuleState = null;
  public selectedSearchFor: WxrlSubjectType = null; // Ahora es un objeto con código y descripción
  public selectedTrigger: WxrlMode = 'Desencadenante';
  public selectedSubject: WxrlSubject = null;
  public ruleText: string = '';

  constructor(private _wxrlRulesService: WrxlRulesService) {}

  /**
   * Determina si el tipo de búsqueda requiere un input de texto
   * 'Regla' (RXRL) y 'Acción' (RXAT) usan input, el resto usa combo
   */
  public get isReglaSearch(): boolean {
    return this.selectedSearchFor?.codSubjectType === 'RXRL' ||
           this.selectedSearchFor?.codSubjectType === 'RXAT';
  }

  /**
   * Determina si el tipo de búsqueda requiere área
   * Solo 'Prueba' (RXPR) requiere área
   */
  public get requiresArea(): boolean {
    return this.selectedSearchFor?.codSubjectType === 'RXPR';
  }

  /**
   * Determina si el tipo de búsqueda permite trigger
   * Solo 'Prueba' (RXPR) permite trigger
   */
  public get allowsTrigger(): boolean {
    return this.selectedSearchFor?.codSubjectType === 'RXPR';
  }

  /**
   * Determina si el tipo de búsqueda es Protocolo
   * Los protocolos solo permiten filtro por laboratorio
   */
  public get isProtocolSearch(): boolean {
    return this.selectedSearchFor?.codSubjectType === 'RXPT';
  }

  /**
   * Determina si el tipo de búsqueda es Acción
   * Las acciones solo permiten filtro por laboratorio y usan input de texto
   */
  public get isActionSearch(): boolean {
    return this.selectedSearchFor?.codSubjectType === 'RXAT';
  }

  /**
   * Determina si se deben deshabilitar los filtros de Área y Estado
   * Protocolo y Acción solo permiten filtro por laboratorio
   */
  public get shouldDisableAreaAndStatus(): boolean {
    return this.isProtocolSearch || this.isActionSearch;
  }

  ngOnInit(): void {
    this._init();
  }

  async _init() {
    // Verificar si hay filtros guardados antes de cargar
    const hasStoredFilters = !!localStorage.getItem(this.STORAGE_KEY);

    try {
      // Cargar laboratorios, estados y tipos de sujeto
      await Promise.all([
        this.loadLaboratories(),
        this.loadStatuses(),
        this.loadSubjectTypes(hasStoredFilters),
      ]);
    } catch (error: any) {
      if (error?.status === 401 || error?.statusCode === 401 || (typeof (error?.error?.message ?? error?.message) === 'string' && /not found in database|Username.*not found/i.test((error?.error?.message ?? error?.message) as string))) {
        this.accessDenied.emit();
        return;
      }
      throw error;
    }

    // Rehidratar filtros después de cargar los datos
    const filtersRehydrated = this.rehydrateFilters();
    
    // Emitir evento de laboratorio seleccionado si hay uno establecido
    if (this.selectedLaboratory?.labNr) {
      this.laboratorySelected.emit(this.selectedLaboratory.labNr);
    }
    
    if (filtersRehydrated) {
      console.log('Filtros rehidratados correctamente');
      // IMPORTANTE: Esperar a que getSubjects termine antes de buscar
      // para que el selectedSubject se rehidrate correctamente
      await this.getSubjects();
      // Ahora ejecutar la búsqueda con todos los filtros correctamente rehidratados
      this.onSearch();
    } else {
      // Si no hay filtros guardados, cargar subjects con valores por defecto
      await this.getSubjects();
    }
  }

  private async loadSubjectTypes(skipDefault: boolean = false): Promise<void> {
    try {
      const types = await this._wxrlRulesService.getSubjectTypes();
      console.log('loadSubjectTypes received:', types);
      this.subjectTypes = types;
      // Solo establecer valor por defecto si NO hay filtros guardados
      // (para evitar sobrescribir el valor que será rehidratado)
      if (!skipDefault) {
      const defaultType = types.find((t) => t.codSubjectType === 'RXPR');
      if (defaultType) {
        this.selectedSearchFor = defaultType;
      } else if (types.length > 0) {
        this.selectedSearchFor = types[0];
        }
      }
      console.log('loadSubjectTypes - subjectTypes array:', this.subjectTypes);
      console.log('loadSubjectTypes - selectedSearchFor:', this.selectedSearchFor);
    } catch (error) {
      console.error('Error cargando subject types:', error);
      this.subjectTypes = [];
    }
  }

  private async loadLaboratories(): Promise<void> {
    const labs = await this._wxrlRulesService.getLaboratories();
    this.laboratories = labs;
    if (labs.length > 0) {
      this.selectedLaboratory = labs[0];
      this.areas = labs[0].areas;
    }
  }

  private async loadStatuses(): Promise<void> {
    const statuses = await this._wxrlRulesService.getStatuses();
    this.statuses = statuses;
    this.selectedStatus = statuses.find(
      (status) => status.codRuleState === 'RXAC'
    );
  }

  /**
   * Deshidrata los filtros actuales a un formato serializable
   */
  private dehydrateFilters(): DehydratedFilters {
    return {
      selectedLaboratoryId: this.selectedLaboratory?.labNr,
      selectedAreaId: this.selectedArea?.labBerNr,
      selectedStatusCode: this.selectedStatus?.codRuleState,
      selectedSearchForCode: this.selectedSearchFor?.codSubjectType, // Guardar código
      selectedTrigger: this.selectedTrigger,
      selectedSubjectValue: this.selectedSubject?.value,
      ruleText: this.ruleText,
    };
  }

  /**
   * Rehidrata los filtros desde localStorage
   */
  private rehydrateFilters(): boolean {
    try {
      const storedFilters = localStorage.getItem(this.STORAGE_KEY);
      if (!storedFilters) {
        // Si no hay filtros guardados, establecer valores por defecto
        this.setDefaultValues();
        return false;
      }

      const dehydratedFilters: DehydratedFilters = JSON.parse(storedFilters);

      // Rehidratar laboratorio
      if (
        dehydratedFilters.selectedLaboratoryId &&
        this.laboratories.length > 0
      ) {
        const lab = this.laboratories.find(
          (l) => l.labNr === dehydratedFilters.selectedLaboratoryId
        );
        if (lab) {
          this.selectedLaboratory = lab;
          this.areas = lab.areas;
        }
      }

      // Rehidratar área
      if (dehydratedFilters.selectedAreaId && this.areas.length > 0) {
        const area = this.areas.find(
          (a) => a.labBerNr === dehydratedFilters.selectedAreaId
        );
        if (area) {
          this.selectedArea = area;
        }
      }

      // Rehidratar estado
      if (dehydratedFilters.selectedStatusCode && this.statuses.length > 0) {
        const status = this.statuses.find(
          (s) => s.codRuleState === dehydratedFilters.selectedStatusCode
        );
        if (status) {
          this.selectedStatus = status;
        }
      }

      // Rehidratar tipo de búsqueda (usando código)
      if (dehydratedFilters.selectedSearchForCode && this.subjectTypes.length > 0) {
        const searchForType = this.subjectTypes.find(
          (t) => t.codSubjectType === dehydratedFilters.selectedSearchForCode
        );
        if (searchForType) {
          this.selectedSearchFor = searchForType;
          if (searchForType.codSubjectType !== 'RXPR') {
            this.selectedTrigger = 'Desencadenante';
          }
        }
      } else if (this.subjectTypes.length > 0) {
        // Si no hay tipo guardado pero hay tipos disponibles, usar el por defecto
        this.setDefaultSearchFor();
      }

      if (dehydratedFilters.selectedTrigger) {
        this.selectedTrigger = dehydratedFilters.selectedTrigger;
      }

      if (dehydratedFilters.ruleText) {
        this.ruleText = dehydratedFilters.ruleText;
      }
    } catch (error) {
      console.error('Error rehidratando filtros:', error);
      // Si hay error, limpiar localStorage y establecer valores por defecto
      localStorage.removeItem(this.STORAGE_KEY);
      this.setDefaultValues();
      return false;
    }
    return true;
  }

  /**
   * Establece los valores por defecto cuando no hay filtros guardados
   */
  private setDefaultValues(): void {
    // Establecer laboratorio por defecto
    if (this.laboratories.length > 0 && !this.selectedLaboratory) {
      this.selectedLaboratory = this.laboratories[0];
      this.areas = this.laboratories[0].areas;
      // Emitir evento de laboratorio seleccionado
      if (this.selectedLaboratory?.labNr) {
        this.laboratorySelected.emit(this.selectedLaboratory.labNr);
      }
    }

    // Establecer estado por defecto
    if (this.statuses.length > 0 && !this.selectedStatus) {
      this.selectedStatus = this.statuses.find(
        (status) => status.codRuleState === 'RXAC'
      );
    }

    // Establecer tipo de búsqueda por defecto
    this.setDefaultSearchFor();
  }

  /**
   * Establece el tipo de búsqueda por defecto (RXPR o el primero disponible)
   */
  private setDefaultSearchFor(): void {
    if (this.subjectTypes.length > 0 && !this.selectedSearchFor) {
      const defaultType = this.subjectTypes.find((t) => t.codSubjectType === 'RXPR');
      if (defaultType) {
        this.selectedSearchFor = defaultType;
      } else {
        this.selectedSearchFor = this.subjectTypes[0];
      }
    }
  }

  /**
   * Guarda los filtros actuales en localStorage
   */
  private saveFiltersToStorage(): void {
    try {
      const dehydratedFilters = this.dehydrateFilters();
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dehydratedFilters));
      console.log('Filtros guardados:', dehydratedFilters);
    } catch (error) {
      console.error('Error guardando filtros:', error);
    }
  }

  private async getSubjects(): Promise<void> {
    if (!this.selectedSearchFor?.codSubjectType) {
      this.subjects = [];
      return;
    }

    try {
      // Si es Regla (RXRL) o Acción (RXAT), no hay subjects (usan input de texto)
      if (this.selectedSearchFor.codSubjectType === 'RXRL' ||
          this.selectedSearchFor.codSubjectType === 'RXAT') {
        this.subjects = [];
        return;
      }

      // Para Protocolo (RXPT), solo se necesita labNr
      // Para otros tipos, se pueden necesitar labBerNr y ruleState
      const labNr = this.selectedLaboratory?.labNr;
      const labBerNr = this.shouldDisableAreaAndStatus ? undefined : this.selectedArea?.labBerNr;
      const ruleState = this.shouldDisableAreaAndStatus ? undefined : this.selectedStatus?.codRuleState;

      const subjects = await this._wxrlRulesService.getSubjects(
        this.selectedSearchFor.codSubjectType, // Enviar código
        labNr,
        labBerNr,
        ruleState
      );

      this.subjects =
        subjects?.filter((subject) => {
          return !!subject.label;
        }) || [];

      // Rehidratar subject seleccionado después de cargar subjects
      const storedFilters = localStorage.getItem(this.STORAGE_KEY);
      if (storedFilters) {
        const dehydratedFilters: DehydratedFilters = JSON.parse(storedFilters);
        if (
          dehydratedFilters.selectedSubjectValue &&
          this.subjects.length > 0
        ) {
          const subject = this.subjects.find(
            (s) => s.value === dehydratedFilters.selectedSubjectValue
          );
          if (subject) {
            this.selectedSubject = subject;
          }
        }
      }

      console.log('Subjects:', this.subjects);
    } catch (error) {
      console.error('Error cargando subjects:', error);
      this.subjects = [];
    }
  }

  public onLaboratoryChange(event: DropdownChangeEvent): void {
    console.log('Selected laboratory:', event.value);
    this.selectedArea = null;
    this.selectedSubject = null;
    this.areas = this.selectedLaboratory?.areas || [];
    this.getSubjects();
    this.saveFiltersToStorage();
    
    // Emitir evento con el labNr del laboratorio seleccionado
    if (this.selectedLaboratory?.labNr) {
      this.laboratorySelected.emit(this.selectedLaboratory.labNr);
    } else {
      this.laboratorySelected.emit(null);
    }
  }

  public onAreaChange(area: any): void {
    this.selectedSubject = null;
    this.getSubjects();
    this.saveFiltersToStorage();
  }

  public onStatusChange(status: any): void {
    this.selectedSubject = null;
    this.getSubjects();
    this.saveFiltersToStorage();
  }

  public onSearchForChange(event: DropdownChangeEvent): void {
    this.selectedArea = null;
    this.selectedSubject = null;
    const selectedType = event.value as WxrlSubjectType;

    // Si es Protocolo o Acción, resetear trigger y limpiar área y estado
    if (selectedType?.codSubjectType === 'RXPT' || selectedType?.codSubjectType === 'RXAT') {
      this.selectedTrigger = 'Desencadenante';
      this.selectedArea = null;
      this.selectedStatus = null;
      // Limpiar ruleText si cambia de Regla a otra cosa, o viceversa
      if (selectedType?.codSubjectType === 'RXAT') {
        this.ruleText = '';
      }
    } else if (selectedType?.codSubjectType !== 'RXPR') {
      this.selectedTrigger = 'Desencadenante';
    }

    this.getSubjects();
    this.saveFiltersToStorage();
  }

  public onTriggerChange(event: any): void {
    this.saveFiltersToStorage();
  }

  public onSubjectChange(subject: any): void {
    this.saveFiltersToStorage();
  }

  public onRuleTextChange(): void {
    this.saveFiltersToStorage();
  }

  onSearch(): void {
    if (!this.selectedSearchFor?.codSubjectType) {
      return;
    }

    const subjectTypeCode = this.selectedSearchFor.codSubjectType;
    // RXAL (Alarma) también usa laboratorio: sin labNr el listado pone selectedLabNr=0
    // y oculta «Crear Nueva Regla» aunque el lab siga seleccionado en UI (#772).
    const actLab = ['RXRL', 'RXPR', 'RXTX', 'RXPT', 'RXAT', 'RXAL'].includes(subjectTypeCode);
    const actArea = ['RXPR'].includes(subjectTypeCode);
    const actTrigger = ['RXPR'].includes(subjectTypeCode);
    console.log({ actLab, actArea, actTrigger });

    // Implement search logic here
    const filters: WxrlFilter = {
      labNr: actLab ? this.selectedLaboratory?.labNr || 0 : 0,
      labBerNr: actArea ? this.selectedArea?.labBerNr || 0 : 0,
      ruleState: this.shouldDisableAreaAndStatus ? undefined : this.selectedStatus?.codRuleState,
      subjectType: subjectTypeCode, // Enviar código en lugar de descripción
      subjectID:
        subjectTypeCode === 'RXRL' || subjectTypeCode === 'RXAT'
          ? this.ruleText
          : this.selectedSubject?.value,
      mode: actTrigger ? this.selectedTrigger : undefined,
    };

    if (subjectTypeCode === 'RXRL' || subjectTypeCode === 'RXAT') {
      this.selectedSubject = null;
    } else {
      this.ruleText = '';
    }
    console.log('Search filters:', filters);

    // Guardar filtros antes de emitir la búsqueda
    this.saveFiltersToStorage();
    this.search.emit(filters);
  }

  /**
   * Método público para limpiar los filtros guardados
   */
  public clearSavedFilters(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    console.log('Filtros guardados eliminados');
  }
}
